import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { encryptData, decryptData } from "../../utils/localEncruption";
import { decryptAPIData } from "../../utils/localEncruption";

// Define the base URL for the API
const BASE_URL1 = `${process.env.REACT_APP_FD1_RATES_URL}`;
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY || "gsDFQwTwuuOXWgh0JG/En4NtZWbahbacdQtlCkMg9tE=";

// Helper function to fetch data from an API
const fetchDataFromAPI = async (url) => {
  const response = await fetch(url, { mode: "cors" });
  const encryptedData = await response.text();
  try {
    const decryptedData = await decryptAPIData(
      encryptedData,
      ENCRYPTION_KEY,
    );
    return decryptedData;
  } catch (error) {
    console.error("Decryption failed for FD rate:", error);
  }

  return response.json();
};

// Helper function to get data from local storage
const getDataFromLocalStorage = (key) => {
  const storedData = localStorage.getItem(key);
  const salt = `${process.env.REACT_APP_SALT}`;

  if (!storedData) {
    return null;
  }

  try {
    const decryptedData = decryptData(storedData, salt);
    return decryptedData;
  } catch (error) {
    return null;
  }
};

// Helper function to save data to local storage
const saveDataToLocalStorage = (key, data) => {
  const salt = `${process.env.REACT_APP_SALT}`;
  const encryptedData = encryptData(data, salt);

  localStorage.setItem(key, encryptedData);
};

// Helper function to get the timestamp when data was last fetched
const getTimestampFromLocalStorage = (key) => {
  const timestamp = localStorage.getItem(`${key}_timestamp`);
  return timestamp ? parseInt(timestamp, 10) : null;
};

// Helper function to save the timestamp when data is fetched
const saveTimestampToLocalStorage = (key) => {
  const timestamp = new Date().getTime();
  localStorage.setItem(`${key}_timestamp`, timestamp);
};

// Helper functions for single bank details caching in localStorage
const getBankDetailsFromLocalStorage = (bankName) => {
  const storedData = localStorage.getItem(`fd_bank_details_${bankName}`);
  const salt = `${process.env.REACT_APP_SALT}`;
  if (!storedData) return null;
  try {
    return decryptData(storedData, salt);
  } catch (error) {
    return null;
  }
};

const saveBankDetailsToLocalStorage = (bankName, data) => {
  const salt = `${process.env.REACT_APP_SALT}`;
  const encryptedData = encryptData(data, salt);
  localStorage.setItem(`fd_bank_details_${bankName}`, encryptedData);
};

// Helper function to clear stale data from localStorage
const clearStaleDataFromLocalStorage = () => {
  localStorage.removeItem("apiData1");
  localStorage.removeItem("apiData2");
  localStorage.removeItem("apiData1_timestamp");
  localStorage.removeItem("apiData2_timestamp");
};

// Cache freshness threshold — 5 minutes.
const DATA_FRESHNESS_THRESHOLD = 5 * 60 * 1000;

// Async thunk to fetch data from both APIs
export const fetchFdRates = createAsyncThunk(
  "fdRateData/fetchFdRates",
  async (_, thunkAPI) => {
    const timestamp1 = getTimestampFromLocalStorage("apiData1");
    const currentTime = new Date().getTime();

    // Check if data in local storage is stale
    const isData1Stale =
      !timestamp1 || currentTime - timestamp1 >= DATA_FRESHNESS_THRESHOLD;

    // If data is NOT stale, try to use cached data
    if (!isData1Stale) {
      const apiData1 = getDataFromLocalStorage("apiData1");
      const apiData2 = getDataFromLocalStorage("apiData2");

      // Only return cached data if BOTH exist
      if (apiData1 && apiData2) {
        console.log("Using cached FD rates data");
        return { apiData1, apiData2 };
      }

      // If either is missing, fall through to fetch new data
      console.log("Cached data incomplete, fetching fresh data");
    } else {
      console.log("Cached data is stale, fetching fresh data");
    }

    // Fetch fresh data from API
    try {
      const data1 = await fetchDataFromAPI(BASE_URL1);

      // Convert all_fd dictionary to flat array for backward compatibility
      const data2 = Object.values(data1.all_fd || {}).flatMap((bank) =>
        bank.rates ? bank.rates.map((rate) => ({ ...bank.meta, ...rate })) : [],
      );

      // Save fetched data and timestamps to local storage
      saveDataToLocalStorage("apiData1", data1);
      saveDataToLocalStorage("apiData2", data2);
      saveTimestampToLocalStorage("apiData1");

      console.log("Fresh FD rates data fetched and cached");
      return { apiData1: data1, apiData2: data2 };
    } catch (error) {
      // Handle API fetch errors gracefully
      console.error("Failed to fetch FD rates from API:", error);

      // On error, try to return cached data even if stale (fallback)
      const apiData1 = getDataFromLocalStorage("apiData1");
      const apiData2 = getDataFromLocalStorage("apiData2");

      if (apiData1 && apiData2) {
        console.warn("API fetch failed, using stale cached data as fallback");
        return { apiData1, apiData2 };
      }

      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

// Async thunk to fetch specific bank details
export const fetchBankDetails = createAsyncThunk(
  "fdRateData/fetchBankDetails",
  async (bankName, thunkAPI) => {
    try {
      const urlObj = new URL(BASE_URL1);
      const targetUrl = `${urlObj.origin}/bank-details/${encodeURIComponent(
        bankName,
      )}/`;

      const data = await fetchDataFromAPI(targetUrl);
      return { bankName, data };
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

// Async thunk to fetch unified bank details (FD + Savings)
export const fetchBankFullDetails = createAsyncThunk(
  "fdRateData/fetchBankFullDetails",
  async (bankName, thunkAPI) => {
    try {
      const urlObj = new URL(BASE_URL1);
      const targetUrl = `${urlObj.origin}/bank-full-details/${encodeURIComponent(
        bankName,
      )}/`;
      const data = await fetchDataFromAPI(targetUrl);
      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);


// Define the initial state with potential local storage data
const initialState = {
  isLoading: false,
  data1: getDataFromLocalStorage("apiData1") || {}, // Data from the first API
  data2: getDataFromLocalStorage("apiData2") || {}, // Data from the second API
  isError: false,
  selectedBankDetails: null, // Store details for the selected bank
  isDetailsLoading: false,
  detailsError: null,
  bankDetailsCache: {}, // In-memory cache for bank details
  selectedBankFullDetails: null,
  isFullDetailsLoading: false,
  fullDetailsError: null,
  selectedFilters: [],
  selectedTenor: "All terms",
  selectedBanks: [],
  showSeniorCitizen: typeof window !== "undefined" && window.innerWidth > 992,
  showSupSeniorCitizen: false,
  sliderState: {
    year: 0,
    month: 0,
    sliderRange: 0,
    isActive: true,
  },
};

// Create slice with additional logic for local storage handling
const fdRateDataSlice = createSlice({
  name: "fdRateData",
  initialState,

  reducers: {
    toggleFilterFD: (state, action) => {
      const selectedCategory = action.payload;
      if (state.selectedFilters.includes(selectedCategory)) {
        state.selectedFilters = state.selectedFilters.filter(
          (element) => element !== selectedCategory,
        );
      } else {
        state.selectedFilters.push(selectedCategory);
      }
    },
    resetFiltersFD: (state) => {
      state.selectedFilters = [];
    },
    selectAllFilters: (state, action) => {
      state.selectedFilters = action.payload;
    },

    setSelectedTenor: (state, action) => {
      state.selectedTenor = action.payload;
    },

    setSliderYear: (state, action) => {
      state.sliderState.year = action.payload;
    },

    setSliderMonth: (state, action) => {
      state.sliderState.month = action.payload;
    },

    setSliderRange: (state, action) => {
      state.sliderState.sliderRange = action.payload;
    },

    resetSliderData: (state) => {
      state.sliderState.sliderRange = 0;
      state.sliderState.year = 0;
      state.sliderState.month = 0;
      state.sliderState.isActive = true;
    },

    setIsActive: (state, action) => {
      state.sliderState.isActive = action.payload;
    },
    setSelectedBanks: (state, action) => {
      state.selectedBanks = action.payload;
    },

    setShowSeniorCitizen: (state, action) => {
      state.showSeniorCitizen = action.payload;
    },

    setShowSupSeniorCitizen: (state, action) => {
      state.showSupSeniorCitizen = action.payload;
    },

    // New action to manually clear cache if needed
    clearFdRatesCache: (state) => {
      clearStaleDataFromLocalStorage();
      state.data1 = {};
      state.data2 = {};
    },

    // Sets a preset tenor (e.g., "1 - 2y") and clears custom slider data
    setPresetTenor: (state, action) => {
      state.selectedTenor = action.payload;
      // Reset slider state mostly, but keep clean
      state.sliderState.year = 0;
      state.sliderState.month = 0;
      state.sliderState.sliderRange = 0;
      state.sliderState.isActive = true; // Marks "Preset" as active
    },

    // Sets custom slider values and clears preset tenor
    setCustomTenure: (state, action) => {
      const { year, month, sliderRange } = action.payload;
      state.sliderState.year = year;
      state.sliderState.month = month;
      state.sliderState.sliderRange = sliderRange;
      state.sliderState.isActive = false; // Marks "Custom" as active
      state.selectedTenor = ""; // Clear radio selection
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchFdRates.pending, (state) => {
      // Only set isLoading to true on initial/empty load to avoid full-screen spinner on revalidation
      if (!state.data1 || Object.keys(state.data1).length === 0) {
        state.isLoading = true;
      }
      state.isError = false;
    });
    builder.addCase(fetchFdRates.fulfilled, (state, action) => {
      state.isLoading = false;
      state.data1 = action.payload.apiData1;
      state.data2 = action.payload.apiData2;
    });
    builder.addCase(fetchFdRates.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      console.error("Failed to fetch FD rates:", action.payload);
    });

    // Bank Details (using SWR pattern)
    builder.addCase(fetchBankDetails.pending, (state, action) => {
      const bankName = action.meta.arg;
      const cached = state.bankDetailsCache[bankName] || getBankDetailsFromLocalStorage(bankName);
      if (cached) {
        state.selectedBankDetails = cached;
        state.isDetailsLoading = false; // Render instantly from cache
      } else {
        state.isDetailsLoading = true;
        state.selectedBankDetails = null; // Clear details for clean load
      }
      state.detailsError = null;
    });
    builder.addCase(fetchBankDetails.fulfilled, (state, action) => {
      state.isDetailsLoading = false;
      const { bankName, data } = action.payload;
      state.selectedBankDetails = data;
      state.bankDetailsCache[bankName] = data; // Cache in memory
      saveBankDetailsToLocalStorage(bankName, data); // Cache in localStorage
    });
    builder.addCase(fetchBankDetails.rejected, (state, action) => {
      state.isDetailsLoading = false;
      state.detailsError = action.payload;
    });

    // Bank Full Details (FD + SD)
    builder.addCase(fetchBankFullDetails.pending, (state) => {
      state.isFullDetailsLoading = true;
      state.fullDetailsError = null;
      state.selectedBankFullDetails = null;
    });
    builder.addCase(fetchBankFullDetails.fulfilled, (state, action) => {
      state.isFullDetailsLoading = false;
      state.selectedBankFullDetails = action.payload;
    });
    builder.addCase(fetchBankFullDetails.rejected, (state, action) => {
      state.isFullDetailsLoading = false;
      state.fullDetailsError = action.payload;
    });
  },
});

export const {
  toggleFilterFD,
  resetFiltersFD,
  setSelectedTenor,
  setSliderYear,
  setSliderMonth,
  setSliderRange,
  resetSliderData,
  setIsActive,
  setSelectedBanks,
  setVisibleColumns,
  selectAllFilters,
  clearFdRatesCache,
  setShowSeniorCitizen,
  setShowSupSeniorCitizen,
  setPresetTenor,
  setCustomTenure,
} = fdRateDataSlice.actions;
export default fdRateDataSlice.reducer;
